import { useCallback, useState } from 'react'
import { Button } from '@mui/material'
import { WandSparkles } from 'lucide-react'
import { useAppSelector } from '@redux/hooks'
import { CreateModal, type CreateModalProps } from './modal'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { DataContainer } from 'dataContainer/data-container'

type DatasetData = Parameters<CreateModalProps['onUpdateDatasetData']>[0]

interface CreateAISubmissionProps {
  template: CreateModalProps['template']
  binderRef: React.MutableRefObject<null>
  dataContainer: DataContainer
}

const transformToDatasetData = (data: DatasetData) => {
  const datasetRows = data.rows.map((row: string[]) => ({
    cvid: '',
    dataset_values: row.map((value: string, idx: number) => ({
      cell_value: value,
      field_id: data.fields[idx],
    })),
  }))

  return {
    dataset_rows: datasetRows,
  }
}

export const CreateAISubmission = ({
  template,
  binderRef,
  dataContainer,
}: CreateAISubmissionProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [hasClaravineAgent = false] = useAppSelector(
    selectFeatureIncludes([FeatureDict.CLARAVINE_AGENT]),
  )

  const handleOpenModal = useCallback(() => {
    setIsOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setIsOpen(false)
  }, [])

  const handleUpdateDatasetData = useCallback(
    (data: DatasetData) => {
      const transformedData = transformToDatasetData(data)
      dataContainer.updateDatasetDataAndTemplateFields({
        binderRef,
        transformedColumns: template?.transformedColumns,
        dataset: transformedData,
        afterUpdateCallback: null,
      })
    },
    [binderRef, dataContainer, template?.transformedColumns],
  )

  const hasDatasetData = dataContainer.initializedRows?.filter(Boolean).length > 0 || false

  if (!hasClaravineAgent || !template) {
    return null
  }

  return (
    <>
      <Button onClick={handleOpenModal} size="large">
        <WandSparkles className="text-purple-800" size={24} />
      </Button>
      {isOpen && (
        <CreateModal
          onClose={handleCloseModal}
          template={template}
          onUpdateDatasetData={handleUpdateDatasetData}
          hasDatasetData={hasDatasetData}
        />
      )}
    </>
  )
}
